import { useState, useCallback, useRef, useEffect } from 'react';
import { CaseStudyItem, CaseStudySnapshot, HistoryState } from '@/types/case-study';

const MAX_HISTORY_SIZE = 50;

/**
 * Create a snapshot of the case study for history tracking
 * Only includes user-editable fields, excludes system fields
 */
function createSnapshot(caseStudy: CaseStudyItem): CaseStudySnapshot {
  return {
    title: caseStudy.title || '',
    description: caseStudy.description || '',
    cover_image: caseStudy.cover_image || '',
    sections: JSON.parse(JSON.stringify(caseStudy.sections || [])), // Deep clone
    is_featured: caseStudy.is_featured || false,
    seo_title: caseStudy.seo_title || '',
    seo_description: caseStudy.seo_description || '',
    og_image: caseStudy.og_image || '',
    status: caseStudy.status || 'draft',
    timestamp: Date.now(),
  };
}

/**
 * Check if two snapshots are equal (excluding timestamp)
 */
function snapshotsEqual(a: CaseStudySnapshot, b: CaseStudySnapshot): boolean {
  const aWithoutTimestamp = { ...a, timestamp: 0 };
  const bWithoutTimestamp = { ...b, timestamp: 0 };
  return JSON.stringify(aWithoutTimestamp) === JSON.stringify(bWithoutTimestamp);
}

/**
 * Restore a case study from a snapshot, preserving system fields
 */
function restoreFromSnapshot(
  currentCaseStudy: CaseStudyItem,
  snapshot: CaseStudySnapshot
): CaseStudyItem {
  return {
    ...currentCaseStudy,
    title: snapshot.title,
    description: snapshot.description,
    cover_image: snapshot.cover_image,
    sections: JSON.parse(JSON.stringify(snapshot.sections)), // Deep clone
    is_featured: snapshot.is_featured,
    seo_title: snapshot.seo_title,
    seo_description: snapshot.seo_description,
    og_image: snapshot.og_image,
    status: snapshot.status,
  };
}

interface UseCaseStudyHistoryOptions {
  enabled?: boolean;
  debounceMs?: number;
}

interface UseCaseStudyHistoryReturn {
  undo: () => CaseStudySnapshot | null;
  redo: () => CaseStudySnapshot | null;
  canUndo: boolean;
  canRedo: boolean;
  currentSnapshot: CaseStudySnapshot;
  wrapSetState: (
    originalSetState: React.Dispatch<React.SetStateAction<CaseStudyItem>>
  ) => (
    update: CaseStudyItem | ((prev: CaseStudyItem) => CaseStudyItem),
    options?: { skipHistory?: boolean }
  ) => void;
  clearHistory: () => void;
}

/**
 * Hook for managing undo/redo history of case study edits
 *
 * Features:
 * - Tracks up to 50 history entries
 * - Debounces rapid changes to prevent history spam
 * - Skips system fields (id, timestamps, slug)
 * - Deep clones sections to prevent reference issues
 * - Provides wrapped setState to track changes
 */
export function useCaseStudyHistory(
  caseStudy: CaseStudyItem,
  options: UseCaseStudyHistoryOptions = {}
): UseCaseStudyHistoryReturn {
  const { enabled = true, debounceMs = 500 } = options;

  const [history, setHistory] = useState<HistoryState>(() => ({
    past: [],
    present: createSnapshot(caseStudy),
    future: [],
  }));

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isUndoRedoRef = useRef(false);
  const lastProcessedSnapshotRef = useRef<CaseStudySnapshot>(createSnapshot(caseStudy));

  /**
   * Add a snapshot to history (with debouncing for text changes)
   */
  const addToHistory = useCallback(
    (snapshot: CaseStudySnapshot, immediate: boolean = false) => {
      if (!enabled || isUndoRedoRef.current) return;

      // Skip if snapshot is identical to last processed
      if (snapshotsEqual(snapshot, lastProcessedSnapshotRef.current)) {
        return;
      }

      const performAdd = () => {
        setHistory((prev) => {
          // Skip if identical to current present
          if (snapshotsEqual(snapshot, prev.present)) {
            return prev;
          }

          lastProcessedSnapshotRef.current = snapshot;

          // Add current present to past, set new present, clear future
          let newPast = [...prev.past, prev.present];

          // Limit history size (FIFO)
          if (newPast.length > MAX_HISTORY_SIZE) {
            newPast = newPast.slice(newPast.length - MAX_HISTORY_SIZE);
          }

          return {
            past: newPast,
            present: snapshot,
            future: [], // Clear redo stack on new change
          };
        });
      };

      if (immediate) {
        // Clear any pending debounced adds
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        performAdd();
      } else {
        // Debounce for text changes
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(performAdd, debounceMs);
      }
    },
    [enabled, debounceMs]
  );

  /**
   * Undo to previous state
   * Returns the snapshot to restore, or null if no history
   */
  const undo = useCallback((): CaseStudySnapshot | null => {
    if (history.past.length === 0) return null;

    isUndoRedoRef.current = true;

    let snapshotToRestore: CaseStudySnapshot | null = null;

    setHistory((prev) => {
      const previous = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, -1);

      snapshotToRestore = previous;

      return {
        past: newPast,
        present: previous,
        future: [prev.present, ...prev.future],
      };
    });

    // Reset flag after state update
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);

    return snapshotToRestore;
  }, [history.past.length]);

  /**
   * Redo to next state
   * Returns the snapshot to restore, or null if no future
   */
  const redo = useCallback((): CaseStudySnapshot | null => {
    if (history.future.length === 0) return null;

    isUndoRedoRef.current = true;

    let snapshotToRestore: CaseStudySnapshot | null = null;

    setHistory((prev) => {
      const next = prev.future[0];
      const newFuture = prev.future.slice(1);

      snapshotToRestore = next;

      return {
        past: [...prev.past, prev.present],
        present: next,
        future: newFuture,
      };
    });

    // Reset flag after state update
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);

    return snapshotToRestore;
  }, [history.future.length]);

  /**
   * Clear all history
   */
  const clearHistory = useCallback(() => {
    setHistory({
      past: [],
      present: createSnapshot(caseStudy),
      future: [],
    });
    lastProcessedSnapshotRef.current = createSnapshot(caseStudy);
  }, [caseStudy]);

  /**
   * Wrap the original setState to track changes in history
   */
  const wrapSetState = useCallback(
    (originalSetState: React.Dispatch<React.SetStateAction<CaseStudyItem>>) => {
      return (
        update: CaseStudyItem | ((prev: CaseStudyItem) => CaseStudyItem),
        options?: { skipHistory?: boolean; immediate?: boolean }
      ) => {
        // Call original setState
        originalSetState((prev) => {
          const nextState = typeof update === 'function' ? update(prev) : update;

          // Track in history unless explicitly skipped or during undo/redo
          if (!options?.skipHistory && !isUndoRedoRef.current && enabled) {
            const snapshot = createSnapshot(nextState);
            addToHistory(snapshot, options?.immediate);
          }

          return nextState;
        });
      };
    },
    [addToHistory, enabled]
  );

  // Sync history present with external case study changes during undo/redo
  useEffect(() => {
    if (isUndoRedoRef.current) {
      // During undo/redo, update the actual case study state
      // This will be handled by the wrapped setState in the parent component
    }
  }, [history.present]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    currentSnapshot: history.present,
    wrapSetState,
    clearHistory,
  };
}

// Export utility functions for testing
export { createSnapshot, snapshotsEqual, restoreFromSnapshot };
